/**
 * EmployeeKycTab — reusable per-employee KYC panel.
 *
 * Renders 4 verification cards (PAN, IFSC + Bank, Document OCR) plus a
 * latest-status badge strip at the top so HR can see at a glance what's
 * been verified.
 *
 * Used in:
 *   - EmployeeProfile (per-employee "KYC" tab)
 *   - KycVerification admin page (when an employee row is expanded)
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  ShieldCheck, IdCard, Landmark, FileSearch,
  Loader2, CheckCircle2, XCircle, AlertCircle, FileText, Upload, Smartphone,
  Mail, Fingerprint,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  verifyPan, verifyIfsc, verifyBankAccount, ocrDocument, getEmployeeKycHistory,
  sendMobileOtp, resendMobileOtp, verifyMobileOtp,
  sendEmailOtp, resendEmailOtp, verifyEmailOtp,
  sendAadhaarOtp, verifyAadhaarOtp,
} from '../api';

const STATUS_STYLES = {
  verified: { color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  failed: { color: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
  needs_review: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertCircle },
  pending: { color: 'bg-slate-100 text-slate-600 border-slate-200', icon: AlertCircle },
};

const VERIFICATION_LABELS = {
  pan: 'PAN',
  ifsc: 'IFSC',
  bank: 'Bank A/C',
  mobile_otp: 'Mobile OTP',
  email_otp: 'Email OTP',
  aadhaar_otp: 'Aadhaar OTP',
  ocr_aadhaar: 'Aadhaar (OCR)',
  ocr_pan: 'PAN (OCR)',
  ocr_passport: 'Passport',
  ocr_driving_license: 'Driving License',
  ocr_educational_certificate: 'Education',
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = s.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${s.color}`} data-testid={`kyc-status-${status}`}>
      <Icon size={11} />
      {status}
    </Badge>
  );
}

export default function EmployeeKycTab({ employeeId, employeeName, employeeEmail }) {
  const [history, setHistory] = useState({ latest_by_type: {}, history: [] });
  const [loadingHistory, setLoadingHistory] = useState(false);

  const refresh = async () => {
    if (!employeeId) return;
    setLoadingHistory(true);
    try {
      const { data } = await getEmployeeKycHistory(employeeId);
      setHistory(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load KYC history');
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!employeeId) return;
      setLoadingHistory(true);
      try {
        const { data } = await getEmployeeKycHistory(employeeId);
        if (!cancelled) setHistory(data);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || 'Failed to load KYC history');
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();
    return () => { cancelled = true; };
  }, [employeeId]);

  const latest = history.latest_by_type || {};

  return (
    <div className="space-y-4" data-testid="employee-kyc-tab">
      {/* Status overview strip */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck size={18} className="text-cyan-600" />
            KYC Status — {employeeName || employeeId}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingHistory && Object.keys(latest).length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 size={14} className="animate-spin" /> Loading status…
            </div>
          ) : Object.keys(latest).length === 0 ? (
            <div className="text-sm text-slate-500">
              No KYC verifications yet. Use the cards below to start.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="kyc-latest-strip">
              {Object.entries(latest).map(([type, rec]) => (
                <div
                  key={type}
                  className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5"
                  title={`Last verified ${new Date(rec.verified_at).toLocaleString()}`}
                >
                  <span className="text-xs font-medium text-slate-700">
                    {VERIFICATION_LABELS[type] || type}
                  </span>
                  <StatusBadge status={rec.status} />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Verification action tabs */}
      <Tabs defaultValue="pan" className="space-y-3">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="pan" data-testid="kyc-tab-pan">
            <IdCard size={14} className="mr-1.5" /> PAN
          </TabsTrigger>
          <TabsTrigger value="bank" data-testid="kyc-tab-bank">
            <Landmark size={14} className="mr-1.5" /> Bank
          </TabsTrigger>
          <TabsTrigger value="mobile" data-testid="kyc-tab-mobile">
            <Smartphone size={14} className="mr-1.5" /> Mobile
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="kyc-tab-email">
            <Mail size={14} className="mr-1.5" /> Email
          </TabsTrigger>
          <TabsTrigger value="aadhaar" data-testid="kyc-tab-aadhaar">
            <Fingerprint size={14} className="mr-1.5" /> Aadhaar
          </TabsTrigger>
          <TabsTrigger value="ocr" data-testid="kyc-tab-ocr">
            <FileSearch size={14} className="mr-1.5" /> OCR
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="kyc-tab-history">
            <FileText size={14} className="mr-1.5" /> History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pan">
          <PanVerifyCard
            employeeId={employeeId}
            defaultName={employeeName}
            onDone={refresh}
          />
        </TabsContent>
        <TabsContent value="bank">
          <BankVerifyCard
            employeeId={employeeId}
            defaultName={employeeName}
            onDone={refresh}
          />
        </TabsContent>
        <TabsContent value="mobile">
          <MobileOtpCard employeeId={employeeId} onDone={refresh} />
        </TabsContent>
        <TabsContent value="email">
          <EmailOtpCard
            employeeId={employeeId}
            defaultEmail={employeeEmail}
            onDone={refresh}
          />
        </TabsContent>
        <TabsContent value="aadhaar">
          <AadhaarOtpCard employeeId={employeeId} onDone={refresh} />
        </TabsContent>
        <TabsContent value="ocr">
          <OcrCard employeeId={employeeId} onDone={refresh} />
        </TabsContent>
        <TabsContent value="history">
          <HistoryCard items={history.history || []} />
        </TabsContent>
      </Tabs>
    </div>
  );
}


// ─── PAN Verification card ──────────────────────────────────────────────


function PanVerifyCard({ employeeId, defaultName, onDone }) {
  const [pan, setPan] = useState('');
  const [name, setName] = useState(defaultName || '');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const handle = async () => {
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan.trim().toUpperCase())) {
      toast.error('PAN format must be 5 letters + 4 digits + 1 letter (e.g. ABCDE1234F)');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await verifyPan({
        pan: pan.trim().toUpperCase(),
        name: name || undefined,
        employee_id: employeeId,
      });
      setResult(data);
      if (data.status === 'verified') {
        toast.success(`PAN verified — registered to ${data.result?.registered_name || 'unknown'}`);
      } else {
        toast.error('PAN verification failed');
      }
      if (onDone) onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.upstream_error || 'PAN verification failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="kyc-pan-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Verify PAN Card</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="text-xs">PAN Number</Label>
          <Input
            value={pan}
            onChange={(e) => setPan(e.target.value.toUpperCase())}
            placeholder="ABCDE1234F"
            maxLength={10}
            data-testid="kyc-pan-input"
            className="font-mono uppercase"
          />
        </div>
        <div>
          <Label className="text-xs">Full Name (optional, for name-match)</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="As per PAN"
            data-testid="kyc-pan-name-input"
          />
        </div>
        <Button
          onClick={handle}
          disabled={busy || !pan}
          className="w-full"
          data-testid="kyc-pan-submit-btn"
        >
          {busy ? <><Loader2 size={14} className="mr-2 animate-spin" /> Verifying…</> : 'Verify PAN'}
        </Button>
        {result && <ResultPanel result={result} />}
      </CardContent>
    </Card>
  );
}


// ─── Bank Account Verification card ────────────────────────────────────


function BankVerifyCard({ employeeId, defaultName, onDone }) {
  const [accountNumber, setAccountNumber] = useState('');
  const [ifsc, setIfsc] = useState('');
  const [name, setName] = useState(defaultName || '');
  const [busyIfsc, setBusyIfsc] = useState(false);
  const [busyBank, setBusyBank] = useState(false);
  const [ifscResult, setIfscResult] = useState(null);
  const [bankResult, setBankResult] = useState(null);

  const handleIfsc = async () => {
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc.trim().toUpperCase())) {
      toast.error('IFSC must be 11 chars: 4 letters + 0 + 6 alphanumeric (e.g. HDFC0001234)');
      return;
    }
    setBusyIfsc(true);
    setIfscResult(null);
    try {
      const { data } = await verifyIfsc({
        ifsc: ifsc.trim().toUpperCase(),
        employee_id: employeeId,
      });
      setIfscResult(data);
      if (data.status === 'verified') {
        toast.success(`${data.result.bank} — ${data.result.branch || ''}`);
      } else {
        toast.error('IFSC not found');
      }
      if (onDone) onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'IFSC lookup failed');
    } finally {
      setBusyIfsc(false);
    }
  };

  const handleBank = async () => {
    if (!accountNumber || !ifsc) {
      toast.error('Account number and IFSC are required');
      return;
    }
    setBusyBank(true);
    setBankResult(null);
    try {
      const { data } = await verifyBankAccount({
        account_number: accountNumber.trim(),
        ifsc: ifsc.trim().toUpperCase(),
        name: name || undefined,
        employee_id: employeeId,
      });
      setBankResult(data);
      if (data.status === 'verified') {
        toast.success(`Bank verified — A/C holder: ${data.result.name_at_bank || 'unknown'}`);
      } else {
        toast.error('Bank verification failed — please double-check details');
      }
      if (onDone) onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bank verification failed');
    } finally {
      setBusyBank(false);
    }
  };

  return (
    <div className="space-y-3">
      <Card data-testid="kyc-ifsc-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Step 1 — Lookup IFSC (free)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              value={ifsc}
              onChange={(e) => setIfsc(e.target.value.toUpperCase())}
              placeholder="HDFC0001234"
              maxLength={11}
              className="font-mono uppercase"
              data-testid="kyc-ifsc-input"
            />
            <Button onClick={handleIfsc} disabled={busyIfsc || !ifsc} data-testid="kyc-ifsc-submit-btn">
              {busyIfsc ? <Loader2 size={14} className="animate-spin" /> : 'Lookup'}
            </Button>
          </div>
          {ifscResult && <ResultPanel result={ifscResult} />}
        </CardContent>
      </Card>

      <Card data-testid="kyc-bank-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Step 2 — Penny-drop Bank Account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">Account Number</Label>
            <Input
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="12345678901234"
              data-testid="kyc-bank-account-input"
              className="font-mono"
            />
          </div>
          <div>
            <Label className="text-xs">Account Holder Name (for name-match)</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="As per bank records"
              data-testid="kyc-bank-name-input"
            />
          </div>
          <Button
            onClick={handleBank}
            disabled={busyBank || !accountNumber || !ifsc}
            className="w-full"
            data-testid="kyc-bank-submit-btn"
          >
            {busyBank
              ? <><Loader2 size={14} className="mr-2 animate-spin" /> Penny-drop in progress…</>
              : 'Verify via Penny-drop'}
          </Button>
          {bankResult && <ResultPanel result={bankResult} />}
        </CardContent>
      </Card>
    </div>
  );
}


// ─── Mobile OTP Card ───────────────────────────────────────────────────


function MobileOtpCard({ employeeId, onDone }) {
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [refId, setRefId] = useState(null);
  const [mobileMasked, setMobileMasked] = useState('');
  const [mocked, setMocked] = useState(false);
  const [debugOtp, setDebugOtp] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [busySend, setBusySend] = useState(false);
  const [busyVerify, setBusyVerify] = useState(false);
  const [result, setResult] = useState(null);

  // We deliberately don't track OTP expiry on the client. The backend
  // returns HTTP 410 when verifying an expired OTP, and the user sees
  // a clear toast — simpler than a per-second ticker that fights the
  // React Compiler strict-mode lint rules.

  const expiresAtLabel = expiresAt
    ? new Date(expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  const handleSend = async () => {
    const digits = mobile.replace(/\D/g, '');
    if (digits.length !== 10) {
      toast.error('Please enter a valid 10-digit Indian mobile number');
      return;
    }
    setBusySend(true);
    setResult(null);
    try {
      const { data } = await sendMobileOtp({ mobile: digits, employee_id: employeeId });
      setRefId(data.ref_id);
      setMobileMasked(data.mobile_masked);
      setMocked(data.mocked);
      setDebugOtp(data.debug_otp);
      setExpiresAt(data.expires_at);
      if (data.mocked) {
        toast.success(`OTP generated (mocked). For dev — code: ${data.debug_otp}`);
      } else {
        toast.success(`OTP sent to ${data.mobile_masked}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setBusySend(false);
    }
  };

  const handleResend = async () => {
    if (!refId) return;
    setBusySend(true);
    try {
      const { data } = await resendMobileOtp({ ref_id: refId });
      setRefId(data.ref_id);
      setMocked(data.mocked);
      setDebugOtp(data.debug_otp);
      setExpiresAt(data.expires_at);
      setOtp('');
      toast.success('Fresh OTP issued');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    } finally {
      setBusySend(false);
    }
  };

  const handleVerify = async () => {
    if (!refId || !otp) {
      toast.error('Please enter the OTP code');
      return;
    }
    setBusyVerify(true);
    try {
      const { data } = await verifyMobileOtp({
        ref_id: refId,
        otp: otp.trim(),
        employee_id: employeeId,
      });
      setResult(data);
      if (data.status === 'verified') {
        toast.success(`Mobile ${mobileMasked} verified`);
        setExpiresAt(null);
        setRefId(null);
      } else {
        toast.error('Incorrect OTP — please try again');
      }
      if (onDone) onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed');
    } finally {
      setBusyVerify(false);
    }
  };

  return (
    <Card data-testid="kyc-mobile-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span>Mobile Number Verification</span>
          {mocked && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
              MSG91 MOCKED — DLT pending
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="text-xs">Mobile Number (10 digits)</Label>
          <div className="flex gap-2">
            <div className="flex items-center px-3 rounded-md border border-slate-300 bg-slate-50 text-sm text-slate-600 font-mono">
              +91
            </div>
            <Input
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="9876543210"
              className="font-mono"
              disabled={!!refId}
              data-testid="kyc-mobile-input"
            />
            <Button
              onClick={refId ? handleResend : handleSend}
              disabled={busySend || (!refId && mobile.length !== 10)}
              data-testid="kyc-mobile-send-btn"
            >
              {busySend ? <Loader2 size={14} className="animate-spin" /> : (refId ? 'Resend' : 'Send OTP')}
            </Button>
          </div>
        </div>

        {refId && (
          <div className="rounded-md border border-cyan-200 bg-cyan-50 p-3 space-y-2" data-testid="kyc-mobile-otp-pane">
            <div className="flex items-center justify-between text-xs">
              <span>
                Sent to <strong>{mobileMasked}</strong>
                {mocked && debugOtp && (
                  <>
                    {' '}— dev OTP: <code className="px-1.5 py-0.5 rounded bg-white border" data-testid="kyc-mobile-debug-otp">{debugOtp}</code>
                  </>
                )}
              </span>
              <span className="font-mono text-slate-600" data-testid="kyc-mobile-countdown">expires at {expiresAtLabel}</span>
            </div>
            <div className="flex gap-2">
              <Input
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit OTP"
                className="font-mono text-center tracking-widest text-base"
                maxLength={6}
                data-testid="kyc-mobile-otp-input"
              />
              <Button
                onClick={handleVerify}
                disabled={busyVerify || otp.length < 4}
                data-testid="kyc-mobile-verify-btn"
              >
                {busyVerify ? <Loader2 size={14} className="animate-spin" /> : 'Verify'}
              </Button>
            </div>
          </div>
        )}

        {result && <ResultPanel result={result} />}
      </CardContent>
    </Card>
  );
}


// ─── Email OTP Card ───────────────────────────────────────────────────


function EmailOtpCard({ employeeId, defaultEmail, onDone }) {
  const [email, setEmail] = useState(defaultEmail || '');
  const [otp, setOtp] = useState('');
  const [refId, setRefId] = useState(null);
  const [emailMasked, setEmailMasked] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [busySend, setBusySend] = useState(false);
  const [busyVerify, setBusyVerify] = useState(false);
  const [result, setResult] = useState(null);

  const expiresAtLabel = expiresAt
    ? new Date(expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  const handleSend = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    setBusySend(true);
    setResult(null);
    try {
      const { data } = await sendEmailOtp({ email: email.trim(), employee_id: employeeId });
      setRefId(data.ref_id);
      setEmailMasked(data.email_masked);
      setExpiresAt(data.expires_at);
      toast.success(`OTP sent to ${data.email_masked}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setBusySend(false);
    }
  };

  const handleResend = async () => {
    if (!refId) return;
    setBusySend(true);
    try {
      const { data } = await resendEmailOtp({ ref_id: refId });
      setRefId(data.ref_id);
      setExpiresAt(data.expires_at);
      setOtp('');
      toast.success('Fresh OTP emailed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    } finally {
      setBusySend(false);
    }
  };

  const handleVerify = async () => {
    if (!refId || !otp) { toast.error('Please enter the OTP code'); return; }
    setBusyVerify(true);
    try {
      const { data } = await verifyEmailOtp({
        ref_id: refId,
        otp: otp.trim(),
        employee_id: employeeId,
      });
      setResult(data);
      if (data.status === 'verified') {
        toast.success(`Email ${emailMasked} verified`);
        setExpiresAt(null);
        setRefId(null);
      } else {
        toast.error('Incorrect OTP — try again');
      }
      if (onDone) onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed');
    } finally {
      setBusyVerify(false);
    }
  };

  return (
    <Card data-testid="kyc-email-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Email Address Verification</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="text-xs">Email Address</Label>
          <div className="flex gap-2">
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="employee@example.com"
              type="email"
              disabled={!!refId}
              data-testid="kyc-email-input"
            />
            <Button
              onClick={refId ? handleResend : handleSend}
              disabled={busySend || (!refId && !email)}
              data-testid="kyc-email-send-btn"
            >
              {busySend ? <Loader2 size={14} className="animate-spin" /> : (refId ? 'Resend' : 'Send OTP')}
            </Button>
          </div>
        </div>

        {refId && (
          <div className="rounded-md border border-cyan-200 bg-cyan-50 p-3 space-y-2" data-testid="kyc-email-otp-pane">
            <div className="flex items-center justify-between text-xs">
              <span>Sent to <strong>{emailMasked}</strong></span>
              <span className="font-mono text-slate-600">expires at {expiresAtLabel}</span>
            </div>
            <div className="flex gap-2">
              <Input
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit OTP"
                className="font-mono text-center tracking-widest text-base"
                maxLength={6}
                data-testid="kyc-email-otp-input"
              />
              <Button
                onClick={handleVerify}
                disabled={busyVerify || otp.length < 4}
                data-testid="kyc-email-verify-btn"
              >
                {busyVerify ? <Loader2 size={14} className="animate-spin" /> : 'Verify'}
              </Button>
            </div>
          </div>
        )}

        {result && <ResultPanel result={result} />}
      </CardContent>
    </Card>
  );
}


// ─── Aadhaar OTP Card ─────────────────────────────────────────────────


function AadhaarOtpCard({ employeeId, onDone }) {
  const [aadhaar, setAadhaar] = useState('');
  const [otp, setOtp] = useState('');
  const [refId, setRefId] = useState(null);
  const [last4, setLast4] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [busySend, setBusySend] = useState(false);
  const [busyVerify, setBusyVerify] = useState(false);
  const [result, setResult] = useState(null);

  const expiresAtLabel = expiresAt
    ? new Date(expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  const handleSend = async () => {
    const digits = aadhaar.replace(/\D/g, '');
    if (digits.length !== 12) {
      toast.error('Aadhaar must be 12 digits');
      return;
    }
    setBusySend(true);
    setResult(null);
    try {
      const { data } = await sendAadhaarOtp({ aadhaar_number: digits, employee_id: employeeId });
      setRefId(data.ref_id);
      setLast4(data.aadhaar_last4);
      setExpiresAt(data.expires_at);
      toast.success(`OTP sent to mobile linked with Aadhaar ****${data.aadhaar_last4}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.upstream_error || 'Failed to send Aadhaar OTP'));
    } finally {
      setBusySend(false);
    }
  };

  const handleVerify = async () => {
    if (!refId || !otp) { toast.error('Please enter the OTP'); return; }
    setBusyVerify(true);
    try {
      const { data } = await verifyAadhaarOtp({
        ref_id: refId,
        otp: otp.trim(),
        employee_id: employeeId,
      });
      setResult(data);
      if (data.status === 'verified') {
        toast.success(`Aadhaar ****${last4} verified`);
        setRefId(null);
      } else {
        toast.error('Aadhaar verification failed');
      }
      if (onDone) onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.upstream_error || 'Verification failed'));
    } finally {
      setBusyVerify(false);
    }
  };

  return (
    <Card data-testid="kyc-aadhaar-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span>Aadhaar OTP eKYC</span>
          <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-[10px]">
            Cashfree · Live API
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="text-xs">Aadhaar Number (12 digits)</Label>
          <Input
            value={aadhaar}
            onChange={(e) => setAadhaar(e.target.value.replace(/\D/g, '').slice(0, 12))}
            placeholder="1234 5678 9012"
            className="font-mono tracking-wider"
            disabled={!!refId}
            maxLength={12}
            data-testid="kyc-aadhaar-input"
          />
          <p className="text-[11px] text-slate-500 mt-1">
            Only the last 4 digits are stored. OTP is delivered to the mobile registered with UIDAI.
          </p>
        </div>
        {!refId && (
          <Button
            onClick={handleSend}
            disabled={busySend || aadhaar.length !== 12}
            className="w-full"
            data-testid="kyc-aadhaar-send-btn"
          >
            {busySend
              ? <><Loader2 size={14} className="mr-2 animate-spin" /> Sending Aadhaar OTP…</>
              : 'Send Aadhaar OTP'}
          </Button>
        )}

        {refId && (
          <div className="rounded-md border border-cyan-200 bg-cyan-50 p-3 space-y-2" data-testid="kyc-aadhaar-otp-pane">
            <div className="flex items-center justify-between text-xs">
              <span>OTP sent for Aadhaar <strong>****{last4}</strong></span>
              <span className="font-mono text-slate-600">expires at {expiresAtLabel}</span>
            </div>
            <div className="flex gap-2">
              <Input
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit OTP"
                className="font-mono text-center tracking-widest text-base"
                maxLength={6}
                data-testid="kyc-aadhaar-otp-input"
              />
              <Button
                onClick={handleVerify}
                disabled={busyVerify || otp.length < 4}
                data-testid="kyc-aadhaar-verify-btn"
              >
                {busyVerify ? <Loader2 size={14} className="animate-spin" /> : 'Verify'}
              </Button>
            </div>
          </div>
        )}

        {result && <ResultPanel result={result} />}
      </CardContent>
    </Card>
  );
}


// ─── OCR Card ──────────────────────────────────────────────────────────


const OCR_DOC_TYPES = [
  { value: 'aadhaar', label: 'Aadhaar Card' },
  { value: 'pan', label: 'PAN Card' },
  { value: 'passport', label: 'Passport' },
  { value: 'driving_license', label: 'Driving License' },
  { value: 'educational_certificate', label: 'Education Certificate' },
];

function OcrCard({ employeeId, onDone }) {
  const [docType, setDocType] = useState('aadhaar');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(f.type)) {
      toast.error('Only JPEG, PNG, or WEBP images are accepted');
      return;
    }
    if (f.size > 12 * 1024 * 1024) {
      toast.error('Image too large — max 12 MB');
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Please upload an image first');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await ocrDocument(docType, file, employeeId);
      setResult(data);
      if (data.status === 'verified') {
        toast.success(`Extracted ${docType} successfully`);
      } else if (data.status === 'needs_review') {
        toast.warning('Extraction confidence is low — please review fields');
      }
      if (onDone) onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'OCR extraction failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="kyc-ocr-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Document OCR (AI-extracted)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label className="text-xs">Document Type</Label>
          <select
            value={docType}
            onChange={(e) => { setDocType(e.target.value); setResult(null); }}
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            data-testid="kyc-ocr-doctype-select"
          >
            {OCR_DOC_TYPES.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs">Upload Image (JPEG / PNG / WEBP, ≤ 12 MB)</Label>
          <label
            className="flex flex-col items-center justify-center cursor-pointer rounded-md border-2 border-dashed border-slate-300 bg-slate-50 py-6 hover:bg-slate-100 transition"
            data-testid="kyc-ocr-file-dropzone"
          >
            <Upload size={20} className="text-slate-400" />
            <span className="mt-2 text-xs text-slate-600">
              {file ? file.name : 'Click to upload or drag-drop an image'}
            </span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFile}
              className="hidden"
              data-testid="kyc-ocr-file-input"
            />
          </label>
        </div>
        {preview && (
          <div className="rounded-md border border-slate-200 overflow-hidden bg-slate-50">
            <img src={preview} alt="preview" className="max-h-56 mx-auto" />
          </div>
        )}
        <Button
          onClick={handleSubmit}
          disabled={busy || !file}
          className="w-full"
          data-testid="kyc-ocr-submit-btn"
        >
          {busy
            ? <><Loader2 size={14} className="mr-2 animate-spin" /> Extracting via Gemini Vision…</>
            : 'Extract Fields'}
        </Button>
        {result && <ResultPanel result={result} />}
      </CardContent>
    </Card>
  );
}


// ─── Reusable result panel ─────────────────────────────────────────────


function ResultPanel({ result }) {
  const isOk = result.status === 'verified';
  const Icon = isOk ? CheckCircle2 : XCircle;
  return (
    <div
      className={`rounded-md border p-3 text-sm ${
        isOk ? 'border-emerald-200 bg-emerald-50' :
        result.status === 'needs_review' ? 'border-amber-200 bg-amber-50' :
        'border-red-200 bg-red-50'
      }`}
      data-testid="kyc-result-panel"
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className={isOk ? 'text-emerald-600' : result.status === 'needs_review' ? 'text-amber-600' : 'text-red-600'} />
        <span className="font-semibold capitalize">{result.status.replace('_', ' ')}</span>
        {result.verified_at && (
          <span className="text-xs text-slate-500 ml-auto">
            {new Date(result.verified_at).toLocaleString()}
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-slate-700">
        {Object.entries(result.result || {}).map(([k, v]) => {
          if (k.startsWith('_') || v === null || v === undefined || v === '') return null;
          const display = typeof v === 'boolean' ? (v ? 'Yes' : 'No') : Array.isArray(v) ? v.join(', ') : String(v);
          return (
            <div key={k} className="flex gap-2">
              <span className="text-slate-500 capitalize">{k.replace(/_/g, ' ')}:</span>
              <span className="font-medium">{display}</span>
            </div>
          );
        })}
      </div>
      {result.error && (
        <div className="mt-2 text-xs text-red-700">⚠ {result.error}</div>
      )}
    </div>
  );
}


// ─── History card ──────────────────────────────────────────────────────


function HistoryCard({ items }) {
  if (!items.length) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
        No verification history yet.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="kyc-history-list">
      {items.map((it) => (
        <Card key={it.id}>
          <CardContent className="py-3">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium">
                    {VERIFICATION_LABELS[it.verification_type] || it.verification_type}
                  </span>
                  <StatusBadge status={it.status} />
                </div>
                <div className="text-xs text-slate-500">
                  By <strong>{it.verified_by_name}</strong> · {new Date(it.verified_at).toLocaleString()}
                </div>
                {it.error && <div className="mt-1 text-xs text-red-600">⚠ {it.error}</div>}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
